"use client";

import { useState, useCallback } from "react";
import Link from "next/link";
import { ArrowRight } from "lucide-react";
import PricingCalculator from "@/components/pricing/PricingCalculator";
import FeatureList from "@/components/pricing/FeatureList";
import { track, HOME_EVENTS } from "@/lib/analytics";
import { PRICE_CENTS_PER_LOCATION } from "@/lib/landing-pricing";

export default function Pricing() {
  const [calc, setCalc] = useState({ locations: 1, totalCents: PRICE_CENTS_PER_LOCATION });

  const handleChange = useCallback((locations: number, totalCents: number) => {
    setCalc({ locations, totalCents });
    track(HOME_EVENTS.PRICING_CHANGE, {
      locations,
      total_cents: totalCents,
      currency: "USD",
    });
  }, []);

  return (
    <section className="bg-white py-20 sm:py-28">
      <div className="mx-auto max-w-6xl px-6">
        <div className="mb-12 text-center">
          <p className="ribbon mb-3 text-ink-500">Simple, honest pricing</p>
          <h2 className="font-display text-4xl font-bold tracking-tight text-ink-950 sm:text-5xl text-balance">
            One price{" "}
            <em className="font-display italic font-normal text-brand-600">
              per location.
            </em>{" "}
            That&apos;s it.
          </h2>
          <p className="mt-4 text-lg text-ink-600 text-pretty">
            More locations? Same flat price. No tiers, no upsells, no
            &ldquo;contact sales&rdquo;.
          </p>
        </div>

        <div className="grid gap-8 lg:grid-cols-[1.1fr_0.9fr] lg:items-start">
          <div className="rounded-3xl border-2 border-ink-100 bg-white p-7 shadow-lg sm:p-9">
            <PricingCalculator onChange={handleChange} />

            <div className="mt-7 border-t border-ink-100 pt-6">
              <Link
                href="/auth/signin"
                onClick={() =>
                  track(HOME_EVENTS.PRICING_CTA_CLICK, {
                    locations: calc.locations,
                    total_cents: calc.totalCents,
                  })
                }
                className="approve-btn inline-flex w-full items-center justify-center gap-2 rounded-xl bg-brand-600 px-7 py-4 text-base font-bold text-white shadow-lg hover:bg-brand-700"
              >
                Start free
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
              <p className="mt-3 text-center text-xs text-ink-500">
                Free up to 3 posts every 30 days · No credit card · Cancel anytime
              </p>
            </div>
          </div>

          <div className="rounded-3xl bg-ink-50 p-7 sm:p-9">
            <p className="ribbon mb-3 text-ink-500">Everything&apos;s included</p>
            <h3 className="font-display text-2xl font-bold text-ink-950">
              No hidden &ldquo;Pro&rdquo; tier. No nickel-and-diming.
            </h3>
            <p className="mt-2 text-sm text-ink-600">
              100 reviews/month per location. Every feature on every plan.
            </p>
            <div className="mt-6">
              <FeatureList />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
